import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Wand2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const noteColors = ["#FFF5F5", "#F4F1EA", "#FBEEE6", "#EEF3EC"];

export default function LoveLetters({ letters, setLetters, names }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [generating, setGenerating] = useState(false);
  const [occasion, setOccasion] = useState("just because");
  const [author, setAuthor] = useState(names[0]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/love-note", { occasion });
      setMessage(data.note);
      if (!title) setTitle("A note for you 💌");
    } catch (e) {
      setMessage("Cupid's a little busy right now — try again in a moment 💕");
    } finally {
      setGenerating(false);
    }
  };

  const save = async (isAi = false) => {
    if (!message.trim()) return;
    const { data } = await api.post("/love-letters", {
      title: title || "A little note",
      message,
      author,
      is_ai: isAi,
    });
    setLetters((l) => [data, ...l]);
    setOpen(false);
    setTitle("");
    setMessage("");
  };

  const remove = async (id) => {
    await api.delete(`/love-letters/${id}`);
    setLetters((l) => l.filter((x) => x.id !== id));
  };

  return (
    <section id="letters" className="bg-[#FFF5F5] border-y border-[#EADEDA]">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-hand text-2xl text-[#D95F59]">words from the heart</p>
            <h2 className="font-display text-4xl tracking-tight">Love Letters</h2>
          </div>
          <button
            data-testid="add-letter-btn"
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-full bg-[#D95F59] text-white px-5 py-2.5 text-sm hover:bg-[#C75D53] hover:scale-105 transition-all"
          >
            <Plus className="w-4 h-4" /> Write a letter
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {letters.map((lt, i) => (
            <motion.div
              key={lt.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
              data-testid="letter-card"
              style={{ background: noteColors[i % noteColors.length] }}
              className="rounded-2xl border border-[#EADEDA] p-6 shadow-soft hover:-translate-y-1 transition-transform group relative"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-xl">{lt.title}</h3>
                {lt.is_ai && (
                  <span className="text-[10px] uppercase tracking-wide bg-[#8C9A86] text-white px-2 py-0.5 rounded-full">
                    AI
                  </span>
                )}
              </div>
              <p className="font-hand text-2xl leading-snug text-[#2A2726]">{lt.message}</p>
              <p className="font-hand text-xl text-[#D95F59] mt-4">— {lt.author}</p>
              <button
                data-testid="delete-letter-btn"
                onClick={() => remove(lt.id)}
                className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4 text-[#D95F59]" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="card-paper rounded-3xl p-7 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="add-letter-modal"
          >
            <h3 className="font-display text-2xl mb-4">Write a love letter</h3>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-sm text-[#756D6A]">From</span>
              {names.map((n) => (
                <button
                  key={n}
                  data-testid={`letter-author-${n}`}
                  onClick={() => setAuthor(n)}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                    author === n ? "bg-[#D95F59] text-white" : "bg-[#F4F1EA] text-[#756D6A]"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <input
              data-testid="letter-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (optional)"
              className="w-full rounded-full border border-[#EADEDA] px-4 py-2.5 bg-[#FFF5F5] outline-none focus:border-[#D95F59] mb-3"
            />
            <textarea
              data-testid="letter-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Pour your heart out…"
              rows={5}
              className="w-full rounded-2xl border border-[#EADEDA] px-4 py-3 bg-[#FFF5F5] outline-none focus:border-[#D95F59] font-hand text-2xl resize-none"
            />

            <div className="flex flex-wrap items-center gap-2 mt-4 p-3 rounded-2xl bg-[#F4F1EA]">
              <Wand2 className="w-4 h-4 text-[#D95F59]" />
              <span className="text-sm text-[#756D6A]">Need help? Let AI write one:</span>
              <input
                data-testid="ai-occasion-input"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                placeholder="occasion"
                className="flex-1 min-w-[120px] rounded-full border border-[#EADEDA] px-3 py-1.5 bg-white text-sm outline-none"
              />
              <button
                data-testid="generate-ai-note-btn"
                onClick={generate}
                disabled={generating}
                className="flex items-center gap-1.5 rounded-full bg-[#8C9A86] text-white px-4 py-1.5 text-sm hover:opacity-90 disabled:opacity-50"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Generate
              </button>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setOpen(false)} className="flex-1 rounded-full border border-[#EADEDA] py-2.5 text-[#756D6A] hover:bg-[#F4F1EA]">
                Cancel
              </button>
              <button
                data-testid="save-letter-btn"
                onClick={() => save(false)}
                disabled={!message.trim()}
                className="flex-1 rounded-full bg-[#D95F59] text-white py-2.5 hover:bg-[#C75D53] disabled:opacity-50"
              >
                Seal it 💌
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
